import { useContext } from 'react'
import styled from 'styled-components'
import { useTranslation, Trans } from 'react-i18next'
import { find, propEq } from 'ramda'
import { breakpoints, colors } from '@/styles'
import { Label, ParagraphMedium, ParagraphRegular } from '@/components/text'
import { Button } from '@/components/button'
import Icon from '@/components/icon'
import Tooltip from '@/components/popper-tooltip'
import {
  PaymentMethod,
  PadInfo,
  showManagePadModal,
  useBank,
  useAccountType,
  formatAccountNumber,
  ListingInfo
} from '@/features/Pad'
import LabeledValue from '@/components/labeled-value'
import { isNullOrEmpty } from '@/utils'
import useGetPadInfo from '@/hooks/use-get-pad-info'
import { SwitchToggle, Dropdown } from '@/components/form'
import config from '@/config'
import ErrorIcon from './circle_x.svg?react'
import Context from '../../view/sign/context'

const { WEB_URL } = config

const PaymentMethodWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const DropdownLabel = styled.span`
  flex: 1;
  margin-right: 30px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 0 0 16px 0;
  }
`

const DropdownWrapper = styled.div`
  flex: 1;
  width: 100%;
`

const PadAccountWrapper = styled.div`
  margin: 16px -20px 0;
  padding: 20px 20px 0;
  border-top: 1px solid ${colors.grey80};
  display: flex;
  flex-direction: column;
`

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
  }
`

const ConnectButton = styled(Button)`
  min-width: unset;
  width: 116px;
  flex-shrink: 0;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin-top: 24px;
    width: 100%;
  }
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 28px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 0;
  }
`

const Title = styled(Label)`
  color: ${colors.grey16};
  display: flex;
  align-items: center;

  svg {
    margin: 0 0 2px 4px;
  }
`

const Text = styled(ParagraphMedium)`
  color: ${colors.grey56};
  margin-top: 8px;
`

const AccountWrapper = styled.div`
  font-size: 14px;
  line-height: 16px;
  div:not(:last-child) {
    margin-bottom: 12px;
  }
`

const AssuranceWrapper = styled.div`
  display: flex;

  svg {
    margin: 4px 12px 0 0;
    flex-shrink: 0;
  }
`

const AssuranceText = styled(ParagraphMedium)`
  color: ${colors.grey08};
  a {
    color: ${colors.link};
  }
`

const SwitchContainer = styled.div`
  padding: 24px 0 0;
  display: flex;
  align-items: center;
  gap: 0 12px;
`

const ErrorBanner = styled.div`
  background: ${colors.red96};
  border: 1px solid ${colors.red48};
  border-radius: 6px;
  color: ${colors.red16};
  margin: 16px 0;
  padding: 16px;
  display: flex;
  > div {
    font-weight: 500;
  }
`

const IconWrapper = styled.div`
  flex: 0 0 32px;
  padding-top: 2px;
  padding-left: 2px;
  color: ${colors.red48};
`

type PadInfoTenantProps = {
  editable: boolean
  paymentMethod: PaymentMethod
  paymentMethodInfo: PadInfo
  listingInfo?: ListingInfo
}

const PadInfoTenant: React.FC<PadInfoTenantProps> = ({
  editable,
  paymentMethod,
  paymentMethodInfo,
  listingInfo
}) => {
  const { t } = useTranslation()
  const { setTenantPaymentMethodSelection, agreePad, setAgreePad, authPad, setAuthPad } =
    useContext(Context)
  const PadMethodSelected = paymentMethod === 'pad'
  const { data } = useGetPadInfo({ enabled: editable })
  const financialInfo = editable ? data?.financial_info : paymentMethodInfo.financial_info
  const bank = useBank(financialInfo?.financial_institution_name)
  const account = useAccountType(financialInfo?.account_type)
  const accountNumber = formatAccountNumber(financialInfo?.account_number)
  const hasPadInfo = !isNullOrEmpty(financialInfo)

  const handleChange = (option: any) => {
    setTenantPaymentMethodSelection(option.value)
  }

  const onSuccess = () => {
    setAgreePad(true)
    setAuthPad(true)
  }

  const handleAddAccount = () => {
    return showManagePadModal({
      listingInfo,
      onSuccess
    })
  }

  const handleEditAccount = () => {
    const onClose = () => {
      setAgreePad(false)
      setAuthPad(false)
    }
    return showManagePadModal({
      padInfo: data,
      listingInfo,
      noEdit: true,
      onSuccess,
      onClose
    })
  }

  const handleAuthPad = () => {
    if (authPad) {
      // Disable switch if toggled on
      setAuthPad(false)
    } else if (agreePad) {
      // If they already agreed, and are have it disabled,
      // re-enabled it
      setAuthPad(true)
    } else {
      // Otherwise prompt for signing
      handleEditAccount()
    }
  }

  const options = [
    {
      value: 'pad',
      label: t('b.contract.edit.payment_method_pad.label', 'PAD (Pre-Authorized Debit)')
    },
    {
      value: 'other',
      label: t('b.contract.edit.payment_method_other.label', 'Other Payment Method')
    }
  ]
  const selection = find(propEq('value', paymentMethod), options)

  return (
    <>
      {editable ? (
        <>
          <PaymentMethodWrapper>
            <DropdownLabel>
              {t('b.contract.edit.requested_payment_method.label', 'Requested payment method')}
            </DropdownLabel>
            <DropdownWrapper>
              <Dropdown onChange={handleChange} options={options} value={selection} />
            </DropdownWrapper>
          </PaymentMethodWrapper>
          {PadMethodSelected && !authPad && (
            <ErrorBanner>
              <IconWrapper>
                <ErrorIcon />
              </IconWrapper>
              <ParagraphMedium>
                {t(
                  'b.contract.edit.requested_payment_method.warn.label',
                  'If you do not wish to authorize rent payments with PAD, please select another Payment method from the options above.'
                )}
              </ParagraphMedium>
            </ErrorBanner>
          )}
        </>
      ) : (
        <LabeledValue
          key="pad-requested"
          label={t('b.contract.edit.requested_payment_method.label', 'Requested payment method')}
          value={t('b.contract.edit.payment_method_pad.label', 'PAD (Pre-Authorized Debit)')}
        />
      )}
      {PadMethodSelected && (editable || hasPadInfo) && (
        <PadAccountWrapper>
          {hasPadInfo ? (
            <>
              <ContentWrapper>
                <TextWrapper>
                  <Title type="large">
                    {editable
                      ? t('b.contract.view.direct_debit.title', 'Direct Debit')
                      : t(
                          'b.contract.view.direct_debit_information.title',
                          'Direct Debit Information'
                        )}
                    {editable && (
                      <Tooltip
                        content={t(
                          'b.contract.view.edit_bank_info.tooltip',
                          'Please go to payment settings to edit direct debit information.'
                        )}
                      >
                        <Icon id="info_blue2" width={17} height={17} />
                      </Tooltip>
                    )}
                  </Title>
                  {editable && (
                    <Text>
                      {t(
                        'b.contract.view.bank_account_funds.text',
                        'Funds will be withdrawn from this bank account and provided to the landlord according to the payment terms specified above.'
                      )}
                    </Text>
                  )}
                </TextWrapper>
              </ContentWrapper>
              <AccountWrapper>
                <LabeledValue
                  label={t('b.contract.view.financial_institution.label', 'Financial institution')}
                  value={bank}
                />
                <LabeledValue
                  label={t('b.contract.view.account_type.label', 'Account type')}
                  value={account}
                />
                <LabeledValue
                  label={t('b.contract.view.account_number.label', 'Account number')}
                  value={accountNumber}
                />
              </AccountWrapper>
              {editable && (
                <SwitchContainer>
                  <SwitchToggle
                    preventToggle
                    name="auth_payment"
                    enabled={authPad}
                    onClick={handleAuthPad}
                  />
                  <ParagraphRegular as="label" htmlFor="auth_payment" onClick={handleAuthPad}>
                    {t('b.contract.view.auth_pad_payments.label', 'Authorize Payments')}
                  </ParagraphRegular>
                </SwitchContainer>
              )}
            </>
          ) : (
            <>
              <ContentWrapper>
                <TextWrapper>
                  <Title type="large">
                    {t('b.contract.view.pad-connect.title', 'Connect your bank account')}
                    <Tooltip
                      content={
                        <Trans i18nKey="l.contract.edit.pad_toggle.tooltip">
                          By opting-in to PAD, the funds for your rent payments will be withdrawn
                          from your account a maximum of seven (7) business days before your payment
                          date and provided to your landlord, on time, every time.
                          <br />
                          <br />
                          PAD makes paying your rent hassle-free and ensures your rent is always on
                          time.
                          <br />
                          <br />
                          For more info, please review our{' '}
                          <a href={`${WEB_URL}/terms`} target="_blank" rel="noopener noreferrer">
                            terms & conditions
                          </a>
                        </Trans>
                      }
                    >
                      <Icon id="info_blue2" width={17} height={17} />
                    </Tooltip>
                  </Title>
                  <Text>
                    {t(
                      'b.contract.view.pad-connect-description.text',
                      'This will connect your bank account and allow you to opt-in to PAD (Pre-Authorized Debits), the most hassle-free way to pay your rent.'
                    )}
                  </Text>
                </TextWrapper>
                <ConnectButton theme="primary" onClick={handleAddAccount}>
                  {t('b.contract.view.pad-connect.button', 'Connect')}
                </ConnectButton>
              </ContentWrapper>
              <AssuranceWrapper>
                <Icon id="secure3" width={18} height={18} />
                <AssuranceText>
                  <Trans i18nKey="b.contract.view.pad-connect-assurance.text">
                    liv.rent protects your personal data, all information is encrypted. Read more
                    about our{' '}
                    <a href="https://liv.rent/privacy" target="_blank" rel="noopener noreferrer">
                      privacy and security.
                    </a>
                  </Trans>
                </AssuranceText>
              </AssuranceWrapper>
            </>
          )}
        </PadAccountWrapper>
      )}
    </>
  )
}

export default PadInfoTenant
